import { useEffect, useRef } from 'react';

/**
 * Intercepts the browser back button (and tab close/refresh) while `active`
 * is true, so an in-progress, unsaved session isn't lost silently.
 *
 * `react-router-dom`'s `useBlocker`/`usePrompt` require a data router
 * (`createBrowserRouter`), but this app uses the classic `<BrowserRouter>`,
 * so blocking is done manually:
 * - A sentinel history entry is pushed while `active`, so the first back
 *   press doesn't leave the page.
 * - On `popstate` while still `active`, another sentinel is pushed
 *   (neutralizing the navigation) and `onBlockedBack` is called so the
 *   caller can show a confirmation UI.
 * - A `beforeunload` listener triggers the browser's native prompt for
 *   tab close/refresh, covering the case a custom modal can't.
 *
 * Leftover sentinel entries after the guard becomes inactive are harmless —
 * revisiting one just re-mounts the current route.
 */
export function useBackNavigationGuard(active: boolean, onBlockedBack: () => void): void {
  const activeRef = useRef(active);
  const onBlockedBackRef = useRef(onBlockedBack);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    onBlockedBackRef.current = onBlockedBack;
  }, [onBlockedBack]);

  useEffect(() => {
    if (!active) return;

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (!activeRef.current) return;
      window.history.pushState(null, '', window.location.href);
      onBlockedBackRef.current();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!activeRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [active]);
}
